import React, { CSSProperties, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { syncProjects } from '../actions';
import { Project } from '../../../models/Project';
import {
  RootReducerState,
  getAllProjects,
  getOffice,
  getProjectsIsFetching,
  getProjectsOverview,
  getUserMe,
  getInternalOfficesByIdState,
  getInternalOfficeIds,
} from '../../../apps/main/rootReducer';
import { distinct, isTemporaryId } from '../../../util';
import { useTranslation } from 'react-i18next';
import {
  FetchProjectContextType,
  OfficeId,
  OfficeRole,
  ProjectId,
} from '../../../models/Types';
import { Office } from '../../../models/Office';
import { createSelector } from 'reselect';
import classNames from 'classnames';
import { makePrioStyles } from '../../../theme/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sortProjects } from '../utils';
import { Select } from '@prio365/prio365-react-library';

const useStyles = makePrioStyles((theme) => ({
  root: {},
  disabledPicker: {
    backgroundColor: 'rgb(0,0,0,0.05)',
  },
  icon: {
    marginRight: theme.old.spacing.unit(1.5),
  },
}));

export const projectSelector = (
  contextType: FetchProjectContextType,
  officeId?: OfficeId,
  officeRoles?: OfficeRole[],
  onlyOfficeProjects?: boolean
) =>
  createSelector<
    [
      (state: RootReducerState) => Project[],
      (state: RootReducerState) => Project[],
      (state: RootReducerState) => { [id: OfficeId]: Office },
      (state: RootReducerState) => OfficeId[],
      (state: RootReducerState) => Office,
      (state: RootReducerState) => {
        [key: string]: OfficeRole[];
      },
    ],
    Project[]
  >(
    getAllProjects,
    getProjectsOverview,
    getInternalOfficesByIdState,
    getInternalOfficeIds,
    (state) => getOffice(state, officeId),
    (state) => getUserMe(state)?.prioData?.officeRoles,
    (
      projectsMe,
      projectsAll,
      officeById,
      officeIds,
      office,
      officeRolesByOfficeId
    ) => {
      if (contextType === 'office') {
        if (officeId) {
          return projectsAll.filter(
            (project) => office?.companyId === project?.subsidiaryId
          );
        }
        const officeProjects = projectsAll.filter((project) =>
          officeIds
            .map((id) => officeById[id])
            .filter((office) => !!office)
            .find(
              (office) =>
                (officeRoles ?? []).find((role) =>
                  officeRolesByOfficeId[office.officeId]?.includes(role)
                ) && office?.companyId === project?.subsidiaryId
            )
        );
        if (onlyOfficeProjects) {
          return officeProjects;
        }
        return distinct([...officeProjects, ...projectsMe]);
      } else {
        return contextType === 'me' ? projectsMe : projectsAll;
      }
    }
  );

interface ProjectPickerPrioCLProps {
  value?: string | string[];
  label?: string;
  onChange?: (value: string | string[]) => void;
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  filter?: (project: Project) => boolean;
  className?: string;
  fetch?: boolean;
  maxTagCount?: number;
  contextType?: FetchProjectContextType;
  officeId?: OfficeId;
  officeRoles?: OfficeRole[];
  onlyOfficeProjects?: boolean;
  onlyProjectIds?: ProjectId[];
  bordered?: boolean;
  dropdownStyle?: CSSProperties;
  includeArchived?: boolean;
  include?: Project | Project[];
  size?: 'small' | 'default' | 'large';
  allowClear?: boolean;
}

export const ProjectPickerPrioCL: React.FC<ProjectPickerPrioCLProps> = (
  props
) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const {
    className,
    value,
    label,
    onChange,
    disabled,
    multiple,
    fetch,
    filter,
    maxTagCount,
    contextType,
    officeId,
    officeRoles,
    onlyOfficeProjects,
    onlyProjectIds,
    dropdownStyle,
    includeArchived = true,
    include,
    size = 'small',
    allowClear = false,
  } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const projectsFromSelector: Project[] = useSelector(
    projectSelector(
      contextType ?? 'me',
      officeId,
      officeRoles,
      onlyOfficeProjects
    )
  );

  const activeProjects = useMemo(
    () =>
      sortProjects(projectsFromSelector).filter(
        (project) =>
          !isTemporaryId(project.projectId) && (!filter || filter(project))
      ),
    [projectsFromSelector, filter]
  );

  const projects = useMemo(() => {
    let allProjects = onlyProjectIds
      ? activeProjects.filter((project) =>
          onlyProjectIds.includes(project.projectId)
        )
      : activeProjects;

    if (include) {
      const includeProjectsArray = Array.isArray(include) ? include : [include];
      includeProjectsArray.forEach((includeProject) => {
        if (
          !allProjects.find(
            (project) => project.projectId === includeProject.projectId
          )
        ) {
          allProjects.push(includeProject);
        }
      });
    }

    return allProjects;
  }, [activeProjects, onlyProjectIds, include]);

  const isFetching = useSelector(getProjectsIsFetching);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleOnChange: (value: string) => void = onChange
    ? (value: string) => {
        onChange(value ?? null);
      }
    : null;
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (fetch) {
      if (contextType !== 'me') {
        dispatch(syncProjects());
      }
    }
  }, [dispatch, fetch, contextType]);
  //#endregion

  return (
    <Select
      allowClear={allowClear}
      size={size}
      // @ts-ignore - optionLabelProp is passed through to rc-select via restProps
      optionLabelProp="title"
      maxTagCount={maxTagCount}
      maxTagPlaceholder={(omitted) =>
        t('common:select.omittedPlaceholder', { count: omitted.length })
      }
      className={classNames(classes.root, className, {
        [classes.disabledPicker]: disabled || isFetching,
      })}
      mode={multiple ? 'multiple' : 'single'}
      showSearch
      value={value}
      placeholder={label}
      onChange={handleOnChange}
      filterOption={(input, option) =>
        option.title.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
      }
      disabled={disabled || isFetching}
      loading={isFetching}
      dropdownStyle={dropdownStyle}
      options={(projects ?? [])
        .filter((project) => includeArchived || !project.isArchived)
        .map((project: Project) => ({
          value: project.projectId,
          label: (
            <>
              {project.parentProject && (
                <FontAwesomeIcon
                  icon={['fal', 'horizontal-rule']}
                  size="sm"
                  className={classes.icon}
                />
              )}
              {`${project.number} ${project.shortName ?? project.name}`}
            </>
          ),
          title: `${project.number} ${project.shortName ?? project.name}`,
        }))}
    />
  );
};

export default ProjectPickerPrioCL;
